// Data sources for Policy chat: named, bounded reads with a fixed row shape.
// A step binds a source to its card, and the chat model reaches the same
// source through `show_view`, so a table in the chat and the rows the model
// reasons over are the same rows.
import { rowsToObjects, runQuery } from "../../../lib/analyticsClient.js";
import { promptCount } from "../../../lib/promptMetrics.js";
import { CLAUDE, CODEX, COPILOT } from "./changes.js";
import { CASE_CAP, readTriageCases } from "./triageCases.js";

const WINDOW_DAYS = 30;
const SERVER_CAP = 25;
const TOP_TOOLS = 5;
const ENDPOINT_CAP = 10;
const ROOT = { alias: "p", entity: "ENTITY_TYPE_PROMPT" };
const p = (field) => ({ stepAlias: "p", field });
const timeRange = { relative: { durationSeconds: String(WINDOW_DAYS * 86400) } };
const MCP_ANY = { pred: { field: p("DIMENSION_FIELD_PROMPT_TOOL_CALL_NAMES"), op: "OPERATOR_LIKE", value: "mcp\\_\\_%" } };

// Provider tags stand for agents. Other clients of the same provider are
// counted too, so these counts are an upper bound for the agent.
const PROVIDER_AGENT = Object.freeze({ anthropic: CLAUDE, openai: CODEX, microsoft: COPILOT, github: COPILOT });

const escapeLike = (s) => String(s).replace(/[\\%_]/g, (c) => `\\${c}`);
const serverPredicate = (id) => ({ pred: { field: p("DIMENSION_FIELD_PROMPT_TOOL_CALL_NAMES"), op: "OPERATOR_LIKE", value: `mcp\\_\\_${escapeLike(id)}\\_\\_%` } });
const distinct = (outputAlias, field, predicate) => ({ outputAlias, computed: { field: p(field), op: "AGGREGATE_OP_COUNT_DISTINCT", predicate } });

/// The server id inside an `mcp__<server>__<tool>` tool name.
export function serverOfTool(name) {
    const match = /^mcp__(.+?)__/.exec(String(name || ""));
    return match ? match[1] : null;
}

/// MCP servers called in the last 30 days, busiest first, at most 25.
/// Rows: { id, name, endpoints, sessions, calls, agents: { [agentId]: endpoints },
/// sessionsBy: { [agentId | "other"]: sessions }, tools: [{ tool, calls }] },
/// with the busiest five tools.
async function readMcpServers({ query = runQuery } = {}) {
    const tools = rowsToObjects(await query({
        root: ROOT,
        timeRange,
        filter: MCP_ANY,
        output: {
            dimensions: [{ field: p("DIMENSION_FIELD_PROMPT_TOOL_CALL_NAMES"), outputAlias: "tool" }],
            metrics: [promptCount("calls")],
        },
    }));
    const byServer = new Map();
    const toolsOf = new Map();
    for (const row of tools) {
        const id = serverOfTool(row.tool);
        if (!id) continue;
        byServer.set(id, (byServer.get(id) ?? 0) + Number(row.calls || 0));
        toolsOf.set(id, [...(toolsOf.get(id) ?? []), { tool: row.tool, calls: Number(row.calls || 0) }]);
    }
    const ids = [...byServer.entries()].sort((a, b) => b[1] - a[1]).slice(0, SERVER_CAP).map(([id]) => id);
    if (!ids.length) return [];
    const metrics = ids.flatMap((id, i) => [
        promptCount(`c${i}`, "p", serverPredicate(id)),
        distinct(`e${i}`, "DIMENSION_FIELD_PROMPT_ENDPOINT_ID", serverPredicate(id)),
        distinct(`s${i}`, "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID", serverPredicate(id)),
    ]);
    const spec = (dimensions) => ({ root: ROOT, timeRange, filter: MCP_ANY, output: { dimensions, metrics } });
    const [totals, perProvider] = await Promise.all([
        query(spec([])).then(rowsToObjects),
        query(spec([{ field: p("DIMENSION_FIELD_PROMPT_PROVIDER"), outputAlias: "provider" }])).then(rowsToObjects),
    ]);
    const total = totals[0] ?? {};
    return ids.map((id, i) => {
        const agents = {};
        const sessionsBy = {};
        for (const row of perProvider) {
            const agent = PROVIDER_AGENT[String(row.provider || "").toLowerCase()];
            const endpoints = Number(row[`e${i}`] || 0);
            const sessions = Number(row[`s${i}`] || 0);
            if (agent && endpoints) agents[agent] = (agents[agent] ?? 0) + endpoints;
            if (sessions) sessionsBy[agent ?? "other"] = (sessionsBy[agent ?? "other"] ?? 0) + sessions;
        }
        return {
            id,
            name: id,
            endpoints: Number(total[`e${i}`] || 0),
            sessions: Number(total[`s${i}`] || 0),
            calls: Number(total[`c${i}`] || 0),
            agents,
            sessionsBy,
            tools: (toolsOf.get(id) ?? []).sort((a, b) => b.calls - a.calls).slice(0, TOP_TOOLS),
        };
    });
}

/// The endpoints that called one MCP server in the last 30 days, busiest
/// first, at most 10. Rows: { id, name (hostname, or the id when the endpoint
/// has no metadata), sessions, calls, last }.
export async function readServerEndpoints(serverId, { query = runQuery } = {}) {
    const rows = rowsToObjects(await query({
        root: { ...ROOT, children: [{ edge: "EDGE_TYPE_PROMPT_ON_ENDPOINT", mode: "JOIN_MODE_LEFT", target: { alias: "e", entity: "ENTITY_TYPE_ENDPOINT" } }] },
        timeRange,
        filter: serverPredicate(serverId),
        output: {
            dimensions: [
                { field: p("DIMENSION_FIELD_PROMPT_ENDPOINT_ID"), outputAlias: "id" },
                { field: { stepAlias: "e", field: "DIMENSION_FIELD_ENDPOINT_HOSTNAME" }, outputAlias: "hostname" },
            ],
            metrics: [
                promptCount("calls"),
                distinct("sessions", "DIMENSION_FIELD_PROMPT_NOTIFICATION_ID"),
                { outputAlias: "last", computed: { field: p("DIMENSION_FIELD_PROMPT_EVENT_TIMESTAMP"), op: "AGGREGATE_OP_MAX" } },
            ],
        },
        orderBy: [{ outputAlias: "calls", direction: "DIRECTION_DESC" }],
        limit: ENDPOINT_CAP,
    }));
    return rows.map((r) => ({ id: String(r.id), name: r.hostname || String(r.id), sessions: Number(r.sessions || 0), calls: Number(r.calls || 0), last: r.last ? String(r.last) : null }));
}

export const SOURCES = Object.freeze({
    mcp_servers: {
        title: `MCP servers, last ${WINDOW_DAYS} days`,
        read: readMcpServers,
        columns: [["Server", "name"], ["Endpoints", "endpoints"], ["Sessions", "sessions"], ["Tool calls", "calls"]],
        note: "Counts come from tool calls named mcp__<server>__<tool>. Agents are inferred from the model provider, so other clients of the same provider are included.",
    },
    triage_cases: {
        title: `Tool calls in Triage cases, last ${WINDOW_DAYS} days`,
        read: () => readTriageCases(),
        columns: [["Group", "name"], ["Cases", "cases"]],
        note: `Cases are findings that met a standard, as Triage lists them, the newest ${CASE_CAP}. Each row groups the tool calls the cases cite by the rule that covers them; a case can be in more than one row. No usage counts exist for the commands themselves.`,
    },
    mcp_server_endpoints: {
        title: `Endpoints that called one MCP server, last ${WINDOW_DAYS} days`,
        needsServer: true,
        note: "Pass server (the server name). The busiest 10 endpoints by tool calls, with hostname, sessions, and last use.",
    },
});

/// Filters and caps rows for `show_view`. Rows stay in the source's shape.
export function filterRows(rows, { agent = null, min_endpoints = null, max_endpoints = null, search = "", limit = 12 } = {}) {
    let out = rows.slice();
    if (agent) out = out.filter((r) => r.agents?.[agent]);
    if (min_endpoints != null) out = out.filter((r) => r.endpoints >= Number(min_endpoints));
    if (max_endpoints != null) out = out.filter((r) => r.endpoints <= Number(max_endpoints));
    const term = String(search || "").trim().toLowerCase();
    if (term) out = out.filter((r) => r.name.toLowerCase().includes(term));
    return out.slice(0, Math.max(1, Math.min(Number(limit) || 12, SERVER_CAP)));
}
